import { ANIMATION_TARGET_ID, isDebug } from "@/constants";
import type { Mesh, Object3D } from "three";
import type { GLTFParser, GLTFReference } from "three-stdlib";

/**
 * This file basically deals with the fact that babylon and threejs
 * have different ways of handling naming of GLTF / GLB loaded meshes.
 *
 * We'd need to learn more about the process of how the artist's animation JSON is
 * created in order to see if we can suggest manual naming or rather it might
 * just be something to consider when threejs / new dice tooling becomes a thing.
 *
 * The ideal future is we do a simple scene search by name and expect
 * animation targetId to match mesh name.
 */

// The existing type doesn't include primitives, but it's there when logging
type GLTFReferenceWithPrimitives = GLTFReference & { primitives?: number };

const assocOf = (obj: Object3D | null, parser: GLTFParser) =>
  obj ? parser?.associations?.get(obj) : undefined;

const gltfNodeName = (
  obj: Object3D | null,
  parser: GLTFParser,
): string | null => {
  const a = assocOf(obj, parser);
  const idx = a?.nodes;
  return idx != null ? (parser.json.nodes?.[idx]?.name ?? null) : null;
};

const isGenericName = (name?: string) =>
  !!name && /^(Mesh|Object|Node|Part)(?:[_\-.]\d+)?$/i.test(name);

const numericSuffixOfParent = (
  child: string | undefined,
  parent: string | undefined,
) => {
  if (!child || !parent) return null;
  const esc = parent.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = child.match(new RegExp(`^${esc}_(\\d+)$`));
  return m ? parseInt(m[1], 10) : null;
};

export const setUserDataAnimationTargets = (
  scene: Object3D,
  parser: GLTFParser,
) => {
  scene.traverse((obj: Object3D) => {
    if (!(obj as Mesh).isMesh) return;

    const parentName = gltfNodeName(obj.parent, parser);
    const selfName = gltfNodeName(obj, parser);

    // Prefer descriptive set names like "d20_Base", "Veins", "d20_Nebula_small"
    const numSuffix = numericSuffixOfParent(
      selfName ?? undefined,
      parentName ?? undefined,
    );
    const selfIsDescriptive =
      !!selfName &&
      selfName !== parentName &&
      // keep an eye on what's a "generic" name..
      !isGenericName(selfName) &&
      numSuffix === null;

    if (selfIsDescriptive) {
      obj.userData[ANIMATION_TARGET_ID] = selfName;

      if (isDebug) {
        console.log(
          `Tagged ${obj.name} with targetId:`,
          obj.userData[ANIMATION_TARGET_ID],
        );
      }

      return;
    }

    // Otherwise use parentName_primitiveX for split / generic meshes
    // This fixes Call of the Corrupted, for example
    let primitive: number | null = null;
    const aMesh = assocOf(obj, parser) as
      | GLTFReferenceWithPrimitives
      | undefined;
    if (aMesh?.primitives !== null) {
      primitive = aMesh?.primitives ?? null;
    }

    if (primitive === null && numSuffix !== null) {
      // handle cases where default mesh ids start with 0 or 1
      primitive = numSuffix === 0 ? 0 : numSuffix - 1;
    }

    if (
      primitive === null &&
      parentName &&
      !selfIsDescriptive &&
      (isGenericName(selfName ?? "") || numSuffix !== null)
    ) {
      const siblings = (obj.parent?.children ?? []).filter(
        (c) => (c as Mesh).isMesh,
      );
      const candidates = siblings.filter((sibling) => {
        const siblingName = sibling.name ?? "";
        return (
          isGenericName(siblingName) ||
          numericSuffixOfParent(siblingName, parentName) !== null
        );
      });
      const index = candidates.indexOf(obj);
      if (index !== -1) {
        primitive = index;
      }
    }

    if (parentName && primitive !== null) {
      obj.userData[ANIMATION_TARGET_ID] = `${parentName}_primitive${primitive}`;

      if (isDebug) {
        console.log(
          `Tagged ${obj.name} with targetId:`,
          obj.userData[ANIMATION_TARGET_ID],
        );
      }

      return;
    }

    // Finally just fallback to the given name or parent name
    // this would likely lead to us investigating a new case to handle if animations don't work
    obj.userData[ANIMATION_TARGET_ID] = selfName ?? parentName;

    if (isDebug) {
      console.log(
        `Tagged ${obj.name} with targetId:`,
        obj.userData[ANIMATION_TARGET_ID],
      );
    }
  });
};
